import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function JourneyHistory() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/northstar/journey-memory`, {
          withCredentials: true,
        });
        if (!cancelled) setItems(res.data?.items || []);
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || "Unable to load journey history.");
          setItems([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div
      data-testid="journey-history-page"
      className="min-h-screen bg-[#0F1720] text-[#F4EEE0] px-6 sm:px-12 lg:px-24 py-10"
    >
      <header className="max-w-3xl mx-auto mb-10">
        <Link
          to="/northstar"
          data-testid="journey-history-back"
          className="text-xs uppercase tracking-widest text-[#A0A8B2] hover:text-[#F4EEE0]"
        >
          ← Northstar
        </Link>
        <h1
          data-testid="journey-history-heading"
          className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-serif"
        >
          Your journey history
        </h1>
        <p className="mt-4 text-base text-[#C7CBD1] max-w-2xl">
          A record of the guided journeys you have completed inside Northstar.
          Only you can see this history.
        </p>
      </header>

      <main className="max-w-3xl mx-auto">
        {items === null && (
          <div
            data-testid="journey-history-loading"
            className="text-sm text-[#A0A8B2]"
          >
            Loading…
          </div>
        )}

        {items !== null && items.length === 0 && (
          <div
            data-testid="journey-history-empty"
            className="border border-[#2A3341] rounded-lg p-8 text-[#C7CBD1]"
          >
            <div className="text-lg font-medium text-[#F4EEE0]">
              No completed journeys yet
            </div>
            <div className="mt-2 text-sm">
              When you complete a guided Northstar journey, it will appear here.
            </div>
          </div>
        )}

        {items !== null && items.length > 0 && (
          <ul
            data-testid="journey-history-list"
            className="space-y-4"
          >
            {items.map((it) => (
              <li
                key={it.id}
                data-testid={`journey-history-item-${it.id}`}
                className="border border-[#2A3341] rounded-lg p-5 hover:border-[#4A5568]"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div
                      data-testid={`journey-history-title-${it.id}`}
                      className="text-lg font-medium text-[#F4EEE0]"
                    >
                      {it.title}
                    </div>
                    <div className="mt-1 text-xs uppercase tracking-widest text-[#A0A8B2]">
                      {it.domain} · v{it.journey_version}
                    </div>
                  </div>
                  <div className="text-right">
                    <div
                      data-testid={`journey-history-date-${it.id}`}
                      className="text-sm text-[#C7CBD1]"
                    >
                      {formatDate(it.completed_at)}
                    </div>
                    <div
                      data-testid={`journey-history-steps-${it.id}`}
                      className="mt-1 text-xs text-[#A0A8B2]"
                    >
                      {it.completed_step_ids?.length ?? 0} steps
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        {error && (
          <div
            data-testid="journey-history-error"
            className="mt-4 text-sm text-[#E9A48A]"
          >
            {error}
          </div>
        )}
      </main>
    </div>
  );
}
